// TelcoTrack — shared client-side column filters for `app-data-table`.

import type {
  DataTableColumn,
  DataTableColumnFilterMode,
  DataTableColumnFilters,
} from './data-table.types';
import { getDataTableSortValue } from './data-table-sort.util';

/** Default distinct-value threshold for `filter: 'auto'` → select dropdown. */
export const DATA_TABLE_FILTER_SELECT_MAX_DEFAULT = 12;

/**
 * Columns are filterable by default.
 * Opt out with `filterable: false`, or via reserved ids (`actions` / `select` / `expand`)
 * when `filterable` is omitted.
 */
export function isDataTableColumnFilterable<T>(column: DataTableColumn<T>): boolean {
  if (column.filterable === false) return false;
  if (column.filterable === true) return true;
  const id = (column.id ?? '').trim().toLowerCase();
  if (id === 'actions' || id === 'select' || id === 'expand') return false;
  // Template-only interactive cells (checkboxes, icon buttons) without a value accessor.
  if (
    column.cell === 'template' &&
    column.stopRowClick === true &&
    !column.format &&
    !column.filterValue &&
    !column.sortValue
  ) {
    return false;
  }
  return true;
}

/**
 * Resolve the raw filter value for a column.
 * Prefer `filterValue`, then the same path as sort (`sortValue` → `format` → property).
 */
export function getDataTableFilterValue<T>(row: T, column: DataTableColumn<T>): unknown {
  if (column.filterValue) {
    return column.filterValue(row);
  }
  return getDataTableSortValue(row, column);
}

/** Display / compare string for filter UI and matching. */
export function formatDataTableFilterScalar(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Ano' : 'Ne';
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return '';
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  const s = String(value).trim();
  if (!s || s === '—') return '';
  return s;
}

function normalizeFilterText(value: string): string {
  return value.trim().toLocaleLowerCase('cs-CZ');
}

function parseLooseNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function coerceFilterNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'string') {
    return parseLooseNumber(value);
  }
  return null;
}

function parseLooseDateMs(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // DD.MM.YYYY
  const cz = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(trimmed);
  if (cz) {
    const d = Date.UTC(Number(cz[3]), Number(cz[2]) - 1, Number(cz[1]));
    return Number.isFinite(d) ? d : null;
  }
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = Date.parse(`${trimmed}T00:00:00.000Z`);
    return Number.isNaN(d) ? null : d;
  }
  const d = Date.parse(trimmed);
  return Number.isNaN(d) ? null : d;
}

function coerceFilterDateMs(value: unknown): number | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') return parseLooseDateMs(value);
  return null;
}

/** Whether any column filter query is non-empty. */
export function hasActiveDataTableColumnFilters(filters: DataTableColumnFilters | null | undefined): boolean {
  if (!filters) return false;
  return Object.values(filters).some((v) => (v ?? '').trim().length > 0);
}

/**
 * Set or clear one column filter. Empty / whitespace removes the key so the
 * full row set is restored and the active badge clears.
 */
export function applyDataTableColumnFilterValue(
  filters: DataTableColumnFilters | null | undefined,
  columnId: string,
  value: string | null | undefined,
): DataTableColumnFilters {
  const next: DataTableColumnFilters = { ...(filters ?? {}) };
  const id = (columnId ?? '').trim();
  if (!id) return next;
  if (!(value ?? '').trim()) {
    delete next[id];
  } else {
    next[id] = String(value);
  }
  return next;
}

/** Keep table chrome mounted whenever the parent still has rows. */
export function dataTableShowTableBlock(opts: {
  loading: boolean;
  errorMessage: string;
  hasInputRows: boolean;
}): boolean {
  return !opts.loading && !opts.errorMessage && opts.hasInputRows;
}

/** Parent-level filters emptied `rows` (not column filters). */
export function dataTableShowEmptyFiltered(opts: {
  loading: boolean;
  errorMessage: string;
  hasInputRows: boolean;
  sourceTotal: number;
}): boolean {
  return !opts.loading && !opts.errorMessage && !opts.hasInputRows && opts.sourceTotal > 0;
}

/** Column filters active and no row matches — keep headers, show inline empty. */
export function dataTableShowColumnFilterEmpty(opts: {
  hasInputRows: boolean;
  hasPipelineRows: boolean;
  hasActiveColumnFilters: boolean;
}): boolean {
  return opts.hasInputRows && !opts.hasPipelineRows && opts.hasActiveColumnFilters;
}

/**
 * Whether a pointer event target is outside the filter trigger and popover
 * (popover may be portaled to `document.body`).
 */
export function isDataTableFilterPointerOutside(
  target: EventTarget | null,
  trigger: { contains(node: Node): boolean } | null | undefined,
  panel: { contains(node: Node): boolean } | null | undefined,
): boolean {
  if (target == null) return false;
  const node = target as Node;
  if (trigger?.contains(node)) return false;
  if (panel?.contains(node)) return false;
  return true;
}

/**
 * Resolve effective filter mode for a column given the current row set
 * (used for `auto` → select when cardinality is small).
 */
export function resolveDataTableColumnFilterMode<T>(
  column: DataTableColumn<T>,
  rows: readonly T[],
): Exclude<DataTableColumnFilterMode, 'auto'> {
  const requested = column.filter ?? 'auto';
  if (requested !== 'auto') return requested;

  const max = column.filterSelectMax ?? DATA_TABLE_FILTER_SELECT_MAX_DEFAULT;
  const distinct = collectDataTableDistinctFilterValues(rows, column);
  if (distinct.length > 0 && distinct.length <= max) {
    return 'select';
  }
  return 'text';
}

/** Sorted unique display values for a select filter (empty cells omitted). */
export function collectDataTableDistinctFilterValues<T>(
  rows: readonly T[],
  column: DataTableColumn<T>,
): string[] {
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    const label = formatDataTableFilterScalar(getDataTableFilterValue(row, column));
    if (label) seen.add(label);
  }
  return Array.from(seen).sort((a, b) =>
    a.localeCompare(b, 'cs-CZ', { numeric: true, sensitivity: 'base' }),
  );
}

function matchTextContains(cell: unknown, query: string): boolean {
  const q = normalizeFilterText(query);
  if (!q) return true;
  const label = normalizeFilterText(formatDataTableFilterScalar(cell));
  return label.includes(q);
}

function matchSelectEquals(cell: unknown, query: string): boolean {
  const q = query.trim();
  if (!q) return true;
  return formatDataTableFilterScalar(cell) === q;
}

/**
 * Number filter:
 * - `10-20` / `10..20` → inclusive range
 * - `>10` / `>=10` / `<10` / `<=10` → comparison
 * - plain number → equality
 * - otherwise → text contains on formatted cell
 */
function matchNumber(cell: unknown, query: string): boolean {
  const q = query.trim();
  if (!q) return true;

  const range = /^(-?\d+(?:[.,]\d+)?)\s*(?:\.\.|–|-|—)\s*(-?\d+(?:[.,]\d+)?)$/.exec(q);
  if (range) {
    const min = parseLooseNumber(range[1]);
    const max = parseLooseNumber(range[2]);
    const n = coerceFilterNumber(cell);
    if (n == null || min == null || max == null) return false;
    return n >= Math.min(min, max) && n <= Math.max(min, max);
  }

  const cmp = /^(>=|<=|>|<)\s*(-?\d+(?:[.,]\d+)?)$/.exec(q);
  if (cmp) {
    const threshold = parseLooseNumber(cmp[2]);
    const n = coerceFilterNumber(cell);
    if (n == null || threshold == null) return false;
    switch (cmp[1]) {
      case '>':
        return n > threshold;
      case '>=':
        return n >= threshold;
      case '<':
        return n < threshold;
      case '<=':
        return n <= threshold;
      default:
        return false;
    }
  }

  const exact = parseLooseNumber(q);
  if (exact != null && /^-?\d+([.,]\d+)?$/.test(q.replace(/\s/g, ''))) {
    const n = coerceFilterNumber(cell);
    return n != null && n === exact;
  }

  return matchTextContains(cell, q);
}

/**
 * Date filter:
 * - single date (ISO / D.M.YYYY) → same calendar day
 * - `from..to` or en/em-dash range (not ASCII `-`, so ISO dates stay whole) → inclusive day range
 * - otherwise → text contains
 */
function matchDate(cell: unknown, query: string): boolean {
  const q = query.trim();
  if (!q) return true;

  // Prefer whole-string date so `2024-01-15` is not split on `-`.
  const day = parseLooseDateMs(q);
  if (day != null) {
    const t = coerceFilterDateMs(cell);
    if (t == null) return matchTextContains(cell, q);
    const next = day + 24 * 60 * 60 * 1000;
    return t >= day && t < next;
  }

  const range = /^(.+?)\s*(?:\.\.|–|—)\s*(.+)$/.exec(q);
  if (range) {
    const from = parseLooseDateMs(range[1]);
    const to = parseLooseDateMs(range[2]);
    const t = coerceFilterDateMs(cell);
    if (from == null || to == null || t == null) {
      return matchTextContains(cell, q);
    }
    const lo = Math.min(from, to);
    const hi = Math.max(from, to) + 24 * 60 * 60 * 1000 - 1;
    return t >= lo && t <= hi;
  }

  return matchTextContains(cell, q);
}

export function rowMatchesDataTableColumnFilter<T>(
  row: T,
  column: DataTableColumn<T>,
  query: string,
  mode: Exclude<DataTableColumnFilterMode, 'auto'>,
): boolean {
  const q = (query ?? '').trim();
  if (!q) return true;
  const cell = getDataTableFilterValue(row, column);
  switch (mode) {
    case 'select':
      return matchSelectEquals(cell, q);
    case 'number':
      return matchNumber(cell, q);
    case 'date':
      return matchDate(cell, q);
    case 'text':
    default:
      return matchTextContains(cell, q);
  }
}

/**
 * Filter rows by active column queries. Empty / unknown / non-filterable columns are ignored.
 * Modes are resolved per column (`auto` uses distinct values from `rows`).
 */
export function filterDataTableRows<T>(
  rows: readonly T[],
  columns: readonly DataTableColumn<T>[],
  filters: DataTableColumnFilters | null | undefined,
): T[] {
  // Keep the same reference when filters are idle — a fresh `.slice()` every CD
  // forces Angular to re-diff large tables even with a stable trackBy.
  if (!rows?.length) {
    return (rows ?? []) as T[];
  }
  if (!hasActiveDataTableColumnFilters(filters)) {
    return rows as T[];
  }

  const active: Array<{
    column: DataTableColumn<T>;
    query: string;
    mode: Exclude<DataTableColumnFilterMode, 'auto'>;
  }> = [];

  for (const column of columns ?? []) {
    if (!isDataTableColumnFilterable(column)) continue;
    const query = (filters?.[column.id] ?? '').trim();
    if (!query) continue;
    active.push({
      column,
      query,
      mode: resolveDataTableColumnFilterMode(column, rows),
    });
  }

  if (!active.length) {
    return rows.slice();
  }

  return rows.filter((row) =>
    active.every(({ column, query, mode }) =>
      rowMatchesDataTableColumnFilter(row, column, query, mode),
    ),
  );
}
